import { giphyDarkGrey } from '@giphy/js-brand'
import { uniqueId } from 'lodash'
import { CSSProperties, useEffect, useRef, useState } from 'react'
import useIsomorphicLayoutEffect from 'react-use/lib/useIsomorphicLayoutEffect'
import { ControlSize, Gradient } from 'types'
import { SelectListProps } from '../select-list'
import { TooltipProps } from '../tooltip/hooks/use-tooltip'
import { Container, Dim, Menu, Options } from './style'

type Props = {
    backgroundColor?: string
    className?: string
    color?: string
    defaultIndex?: number
    disabled?: boolean
    gradient?: Gradient
    index?: number
    options: string[]
    selectListOptions?: Partial<SelectListProps>
    size?: ControlSize
    style?: CSSProperties
    tooltipOptions?: Partial<TooltipProps>
    onChange?: (index: number) => void
}

const SegmentedControl = ({
    backgroundColor = giphyDarkGrey,
    className,
    color = '#fff',
    defaultIndex,
    disabled = false,
    gradient,
    index: indexProp,
    options,
    selectListOptions,
    size = 'small',
    style,
    tooltipOptions,
    onChange,
}: Props) => {
    const initialValueRef = useRef<number | undefined>(indexProp || defaultIndex)
    const optionsRef = useRef<HTMLDivElement>(null)
    const idRef = useRef<string>(uniqueId('segmented-control'))
    const [activeIndex, setActiveIndex] = useState<number | undefined>(indexProp || defaultIndex)
    const [isTruncated, setIsTruncated] = useState<boolean>(false)

    useIsomorphicLayoutEffect(() => {
        if (!optionsRef.current) return

        setIsTruncated(optionsRef.current.scrollWidth > optionsRef.current.offsetWidth)
    }, [optionsRef])

    useEffect(() => {
        if (typeof indexProp === 'number' && indexProp !== activeIndex) {
            setActiveIndex(indexProp)
        }
    }, [indexProp])

    useEffect(() => {
        if (typeof activeIndex === 'number' && activeIndex !== initialValueRef.current) {
            initialValueRef.current = undefined
            onChange?.(activeIndex)
        }
    }, [activeIndex])

    return (
        <Container
            className={className}
            backgroundColor={backgroundColor}
            controlSize={size}
            disabled={disabled}
            style={style}
        >
            <Options
                activeIndex={activeIndex}
                backgroundColor={backgroundColor}
                controlColor={color}
                controlId={idRef.current}
                controlSize={size}
                gradient={gradient}
                isTruncated={isTruncated}
                options={options}
                ref={optionsRef}
                onChange={setActiveIndex}
            />
            {isTruncated && (
                <>
                    <Dim backgroundColor={backgroundColor} controlSize={size} />
                    <Menu
                        activeIndex={activeIndex}
                        backgroundColor={backgroundColor}
                        controlColor={color}
                        controlId={idRef.current}
                        options={options}
                        selectListOptions={selectListOptions}
                        tooltipOptions={tooltipOptions}
                        onChange={setActiveIndex}
                    />
                </>
            )}
        </Container>
    )
}

export default SegmentedControl
